if (!customElements.get("pf-pilot-product-form")) {
  customElements.define(
    "pf-pilot-product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true" || this.pfCartCommitInFlight)
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        window.showSpinner(this);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const complementaryVariantIds = this.complementaryVariantIds(formData);
        const isPfAtomicCommit = this.isPfConfiguratorForm();
        formData.delete("pf_scroll_complementary_variant_id[]");
        delete config.headers["Content-Type"];
        config.body = formData;

        if (isPfAtomicCommit) this.pfCartCommitInFlight = true;

        const addRequest = isPfAtomicCommit
          ? this.commitPfCartAtomically(formData, complementaryVariantIds)
          : this.addComplementaryProducts(complementaryVariantIds)
              .then(() => fetch(`${routes.cart_add_url}`, config))
              .then((response) => response.json());

        addRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker("add:wait-for-subscribers");
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker("add:wait-for-subscribers", startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            if (isPfAtomicCommit) {
              this.handlePfAtomicCartError(e, formData);
              return;
            }
            if (e?.response?.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: e.response.errors || e.response.description,
                message: e.response.message,
              });
              this.handleErrorMessage(e.response.description);
              this.error = true;
              return;
            }
            console.error(e); // eslint-disable-line no-console
          })
          .finally(() => {
            if (isPfAtomicCommit) this.pfCartCommitInFlight = false;
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            window.hideSpinner(this);

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable, text) {
        const shouldDisable = disable ?? true;
        if (shouldDisable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      complementaryVariantIds(formData) {
        return Array.from(
          new Set(formData.getAll("pf_scroll_complementary_variant_id[]").filter(Boolean))
        );
      }

      isPfConfiguratorForm() {
        return Boolean(this.closest?.('[data-pf-experience="pf_scroll_pilot"]'));
      }

      pfCartUrl(path) {
        const root = window.Shopify?.routes?.root || "/";
        return `${root.endsWith("/") ? root : `${root}/`}${path.replace(/^\//, "")}`;
      }

      pfCartError(message, response = null) {
        const error = new Error(message);
        if (response) error.response = response;
        return error;
      }

      async pfFetchJson(url, options = {}, fallbackMessage = "The cart could not be updated.") {
        let response;
        try {
          response = await fetch(url, {
            credentials: "same-origin",
            ...options,
          });
        } catch (error) {
          throw this.pfCartError(`${fallbackMessage} Check your connection and try again.`, {
            status: 0,
            description: error?.message || fallbackMessage,
          });
        }

        let body;
        try {
          body = await response.json();
        } catch {
          throw this.pfCartError(fallbackMessage, {
            status: response.status || 500,
            description: fallbackMessage,
          });
        }

        if (!response.ok || body?.status) {
          throw this.pfCartError(body?.description || body?.message || fallbackMessage, {
            ...body,
            status: body?.status || response.status,
            description: body?.description || body?.message || fallbackMessage,
          });
        }

        return body;
      }

      pfJsonRequest(body) {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.body = JSON.stringify(body);
        return config;
      }

      // Right after an add, Shopify can briefly serve a cart without the new line while another
      // script's cart update settles (seen on page load, Nov 2), so a cart that's missing the line or
      // its quantity is read again a few times before the add counts as failed and is backed out.
      async fetchPfCartAfterAdd(request, snapshot) {
        const expected = this.expectedPfCartQuantities(snapshot, request.items);
        for (let attempt = 0; ; attempt += 1) {
          const cart = await this.fetchPfCart();
          try {
            const line = this.pfMainAddedLine(cart, request);
            this.assertPfCartQuantities(cart, expected);
            return { cart, line };
          } catch (error) {
            if (attempt >= 4) throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
        }
      }

      fetchPfCart() {
        return this.pfFetchJson(
          this.pfCartUrl("cart.js"),
          {
            cache: "no-store",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
          },
          "The current cart could not be verified."
        );
      }

      normalizePfProperties(properties = {}) {
        return Object.fromEntries(
          Object.entries(properties)
            .filter(([, value]) => value !== null && value !== undefined && value !== "")
            .map(([key, value]) => [String(key), String(value)])
            .sort(([left], [right]) => left.localeCompare(right))
        );
      }

      normalizePfAttributes(attributes = {}) {
        const volatileIntegrationAttributes = new Set(
          [
            "_em_device_id",
            "_em_nav_id",
            "_em_session_id",
            "ftkn",
            "GLBE_SESS_ID",
            "GE_frtToken",
            "GE_isApplePay",
            "GE_consentPrefs",
            "GE_utmParams",
            "GE_clientId",
            "cartToken",
            "vct",
            "vcd",
            "vsg",
            // Written on every page load (Kount fraud session, the theme's recently viewed upsell),
            // so an add tapped in the first seconds read them as another tab's edit (Nov 2, F10).
            "KOUNT_SESSION_ID",
            "__upsell_recently_viewed_products",
            "sb-cart-analytics-id",
            "sb-li-offers-shown",
            "EdgeTagId",
            "EdgeTagDbId",
          ].map((key) => key.toLowerCase())
        );
        return Object.fromEntries(
          Object.entries(attributes)
            .filter(([key]) => !volatileIntegrationAttributes.has(String(key).toLowerCase()))
            .map(([key, value]) => [String(key), String(value ?? "")])
            .sort(([left], [right]) => left.localeCompare(right))
        );
      }

      pfFormValue(value, name) {
        if (typeof File !== "undefined" && value instanceof File) {
          if (value.size === 0) return "";
          throw this.pfCartError(
            `The selected file in ${name} cannot be committed with the configured add-ons.`
          );
        }
        return String(value ?? "");
      }

      buildPfAtomicItems(formData, complementaryVariantIds) {
        const mainVariantId = this.pfFormValue(formData.get("id"), "id").trim();
        const mainQuantityValue = this.pfFormValue(
          formData.get("quantity") || "1",
          "quantity"
        ).trim();
        const mainQuantity = Number(mainQuantityValue);
        if (
          !/^\d+$/.test(mainVariantId) ||
          !/^\d+$/.test(mainQuantityValue) ||
          !Number.isInteger(mainQuantity) ||
          mainQuantity < 1
        ) {
          throw this.pfCartError("The selected product quantity or variant is invalid.");
        }

        const mainItem = { id: mainVariantId, quantity: mainQuantity };
        const mainSellingPlan = this.pfFormValue(
          formData.get("selling_plan") || "",
          "selling_plan"
        ).trim();
        if (mainSellingPlan) {
          if (!/^\d+$/.test(mainSellingPlan)) {
            throw this.pfCartError("The main product selling plan is invalid.");
          }
          mainItem.selling_plan = mainSellingPlan;
        }

        const mainProperties = {};
        const indexedItems = new Map();

        Array.from(formData.entries()).forEach(([name, rawValue]) => {
          const value = this.pfFormValue(rawValue, name);
          if (name === "note" || /^attributes\[.+\]$/.test(name)) {
            throw this.pfCartError(
              "PF Scroll integrations cannot mutate cart notes or attributes. Use line properties instead."
            );
          }
          let match = name.match(/^properties\[(.+)\]$/);
          if (match) {
            mainProperties[match[1]] = value;
            return;
          }
          match = name.match(
            /^items\[(\d+)\]\[(id|quantity|selling_plan|parent_id|parent_line_key)\]$/
          );
          if (match) {
            const index = Number(match[1]);
            const item = indexedItems.get(index) || { quantity: 1, properties: {} };
            item[match[2]] = value;
            indexedItems.set(index, item);
            return;
          }
          match = name.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);
          if (match) {
            const index = Number(match[1]);
            const item = indexedItems.get(index) || { quantity: 1, properties: {} };
            item.properties[match[2]] = value;
            indexedItems.set(index, item);
          }
        });

        const normalizedMainProperties = this.normalizePfProperties(mainProperties);
        if (Object.keys(normalizedMainProperties).length)
          mainItem.properties = normalizedMainProperties;

        const appItems = Array.from(indexedItems.entries())
          .sort(([left], [right]) => left - right)
          .map(([index, item]) => {
            const quantityValue = String(item.quantity || "1").trim();
            const normalized = {
              id: String(item.id || "").trim(),
              quantity: Number(quantityValue),
            };
            if (
              !/^\d+$/.test(normalized.id) ||
              !/^\d+$/.test(quantityValue) ||
              !Number.isInteger(normalized.quantity) ||
              normalized.quantity < 1
            ) {
              throw this.pfCartError(`Integration line ${index + 1} is invalid.`);
            }
            if (item.selling_plan) {
              if (!/^\d+$/.test(String(item.selling_plan))) {
                throw this.pfCartError(
                  `Integration line ${index + 1} has an invalid selling plan.`
                );
              }
              normalized.selling_plan = String(item.selling_plan);
            }
            if (item.parent_id) {
              if (!/^\d+$/.test(String(item.parent_id))) {
                throw this.pfCartError(`Integration line ${index + 1} has an invalid parent ID.`);
              }
              normalized.parent_id = String(item.parent_id);
            }
            if (item.parent_line_key) {
              normalized.parent_line_key = String(item.parent_line_key).trim();
              if (!normalized.parent_line_key) {
                throw this.pfCartError(
                  `Integration line ${index + 1} has an empty parent line key.`
                );
              }
            }
            if (normalized.parent_id && normalized.parent_line_key) {
              throw this.pfCartError(
                `Integration line ${index + 1} cannot use both parent reference types.`
              );
            }
            const properties = this.normalizePfProperties(item.properties);
            if (Object.keys(properties).length) normalized.properties = properties;
            return { index, item: normalized, role: "integration" };
          });

        const matchingMainLine = appItems.find(({ item }) => item.id === mainVariantId);
        if (matchingMainLine) {
          matchingMainLine.role = "main";
          matchingMainLine.item.quantity = mainQuantity;
          matchingMainLine.item.properties = {
            ...normalizedMainProperties,
            ...(matchingMainLine.item.properties || {}),
          };
          if (!Object.keys(matchingMainLine.item.properties).length)
            delete matchingMainLine.item.properties;
          if (mainSellingPlan && !matchingMainLine.item.selling_plan)
            matchingMainLine.item.selling_plan = mainSellingPlan;
        } else {
          appItems.unshift({ index: -1, item: mainItem, role: "main" });
        }

        const complementaryIds = new Set(complementaryVariantIds.map(String));
        complementaryIds.forEach((variantId) => {
          if (!/^\d+$/.test(variantId) || variantId === mainVariantId) {
            throw this.pfCartError("A selected add-on has an invalid variant.");
          }
          appItems.push({
            index: Number.POSITIVE_INFINITY,
            item: { id: variantId, quantity: 1 },
            role: "accessory",
          });
        });

        const lines = appItems;
        const items = lines.map(({ item }) => item);

        return {
          complementaryIds,
          items,
          lines,
          mainItem: lines.find(({ role }) => role === "main")?.item,
          mainVariantId,
        };
      }

      validatePfParentReferences(request, snapshot) {
        request.lines.forEach(({ index, item, role }) => {
          const label = role === "main" ? "main product" : `${role} line ${index + 1}`;
          if (item.parent_id) {
            const parents = request.items.filter(
              (candidate) => candidate !== item && String(candidate.id) === String(item.parent_id)
            );
            if (parents.length !== 1) {
              throw this.pfCartError(
                `The ${label} parent ID must identify exactly one line in the same request.`
              );
            }
          }
          if (
            item.parent_line_key &&
            !(snapshot.items || []).some((candidate) => candidate.key === item.parent_line_key)
          ) {
            throw this.pfCartError(
              `The ${label} parent line no longer exists in the pre-cart snapshot.`
            );
          }
        });
      }

      pfStableValue(value) {
        if (Array.isArray(value)) {
          return value.map((entry) => this.pfStableValue(entry));
        }
        if (value && typeof value === "object") {
          return Object.fromEntries(
            Object.entries(value)
              .filter(([key]) => !/(^|_)key$|^(created|updated)_at$/i.test(key))
              .sort(([left], [right]) => left.localeCompare(right))
              .map(([key, entry]) => [key, this.pfStableValue(entry)])
          );
        }
        return value ?? null;
      }

      pfBaseLineState(item) {
        return {
          properties: this.normalizePfProperties(item.properties || {}),
          sellingPlanId: String(
            item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ""
          ),
          variantId: String(item.variant_id || item.id || ""),
        };
      }

      pfRelationshipParentKey(value) {
        if (Array.isArray(value)) {
          return value.map((entry) => this.pfRelationshipParentKey(entry)).find(Boolean) || "";
        }
        if (!value || typeof value !== "object") return "";
        return Object.entries(value).reduce((parentKey, [name, entry]) => {
          if (parentKey) return parentKey;
          if (/^(parent_key|parentLineKey|parent_line_key)$/i.test(name) && entry) {
            return String(entry);
          }
          return this.pfRelationshipParentKey(entry);
        }, "");
      }

      pfResolveParentLine(item, contextItems = []) {
        const parentKey =
          item.parent_relationship?.parent_key ||
          item.parent_line_key ||
          this.pfRelationshipParentKey(item.relationships) ||
          "";
        if (parentKey) {
          return contextItems.find((candidate) => candidate.key === parentKey) || null;
        }
        const parentId = item.parent_relationship?.parent_id || item.parent_id || "";
        if (!parentId) return null;
        const candidates = contextItems.filter(
          (candidate) =>
            candidate !== item && String(candidate.variant_id || candidate.id) === String(parentId)
        );
        const sameRequestCandidates = candidates.filter((candidate) => !candidate.key);
        if (sameRequestCandidates.length === 1) return sameRequestCandidates[0];
        return candidates.length === 1 ? candidates[0] : null;
      }

      pfRelationshipState(item, contextItems = []) {
        const parent = this.pfResolveParentLine(item, contextItems);
        const rawParentReference =
          item.parent_relationship?.parent_key ||
          item.parent_line_key ||
          this.pfRelationshipParentKey(item.relationships) ||
          item.parent_relationship?.parent_id ||
          item.parent_id ||
          "";
        return {
          parent: parent ? this.pfBaseLineState(parent) : null,
          relationships: this.pfStableValue(item.relationships || null),
          unresolvedParent: parent || !rawParentReference ? "" : String(rawParentReference),
        };
      }

      validatePfSnapshotRelationships(snapshot) {
        const items = snapshot.items || [];
        items.forEach((item) => {
          const rawParentReference =
            item.parent_relationship?.parent_key ||
            item.parent_relationship?.parent_id ||
            item.parent_line_key ||
            item.parent_id ||
            this.pfRelationshipParentKey(item.relationships);
          if (!rawParentReference) return;
          const parent = this.pfResolveParentLine(item, items);
          if (!parent) {
            throw this.pfCartError(
              "An existing nested cart line has an unresolved parent relationship."
            );
          }
          const sameVariantParents = items.filter(
            (candidate) =>
              candidate !== item &&
              String(candidate.variant_id || candidate.id) ===
                String(parent.variant_id || parent.id)
          );
          if (sameVariantParents.length !== 1) {
            throw this.pfCartError(
              "Existing nested cart lines have ambiguous duplicate parent variants and cannot be reconstructed safely."
            );
          }
        });
      }

      pfLineSignature(item, contextItems = [item]) {
        const relationship = this.pfRelationshipState(item, contextItems);
        return JSON.stringify({
          ...this.pfBaseLineState(item),
          parent: relationship.parent,
          unresolvedParent: relationship.unresolvedParent,
        });
      }

      aggregatePfCartLines(items = [], contextItems = items) {
        return items.reduce((groups, item) => {
          const signature = this.pfLineSignature(item, contextItems);
          const group = groups.get(signature) || { lines: [], quantity: 0 };
          group.lines.push(item);
          group.quantity += Number(item.quantity) || 0;
          groups.set(signature, group);
          return groups;
        }, new Map());
      }

      expectedPfCartQuantities(snapshot, requestItems) {
        const requestContext = [...(snapshot.items || []), ...requestItems];
        const expected = new Map(
          Array.from(this.aggregatePfCartLines(snapshot.items).entries()).map(
            ([signature, group]) => [signature, group.quantity]
          )
        );
        requestItems.forEach((item) => {
          const signature = this.pfLineSignature(item, requestContext);
          expected.set(signature, (expected.get(signature) || 0) + Number(item.quantity || 1));
        });
        return expected;
      }

      assertPfCartQuantities(cart, expected, lineLabel = "cart") {
        const actual = this.aggregatePfCartLines(cart.items || []);
        const signatures = new Set([...expected.keys(), ...actual.keys()]);
        const mismatch = Array.from(signatures).some(
          (signature) => (expected.get(signature) || 0) !== (actual.get(signature)?.quantity || 0)
        );
        if (mismatch) {
          throw this.pfCartError(
            `The ${lineLabel} did not contain the exact products and quantities requested.`
          );
        }
      }

      pfRollbackStateIsRecognized(cart, snapshot, request) {
        if (this.pfCartSnapshotMatches(cart, snapshot)) return true;
        try {
          this.assertPfRollbackExpectedState(cart, snapshot, request);
          return true;
        } catch {
          return false;
        }
      }

      pfLineMaterialState(item, contextItems = [item]) {
        const instructions = item.instructions || null;
        return {
          ...this.pfBaseLineState(item),
          canRemove:
            item.can_remove ??
            item.canRemove ??
            instructions?.can_remove ??
            instructions?.canRemove ??
            null,
          canUpdateQuantity:
            item.can_update_quantity ??
            item.canUpdateQuantity ??
            instructions?.can_update_quantity ??
            instructions?.canUpdateQuantity ??
            null,
          compareAtPrice: item.compare_at_price ?? null,
          discountedPrice: item.discounted_price ?? null,
          discounts: this.pfStableValue(item.discounts || []),
          finalLinePrice: item.final_line_price ?? null,
          finalPrice: item.final_price ?? null,
          instructions: this.pfStableValue(instructions),
          lineDiscountAllocations: this.pfStableValue(item.line_level_discount_allocations || []),
          linePrice: item.line_price ?? null,
          originalLinePrice: item.original_line_price ?? null,
          originalPrice: item.original_price ?? item.price ?? null,
          quantity: Number(item.quantity) || 0,
          relationship: this.pfRelationshipState(item, contextItems),
          totalDiscount: item.total_discount ?? item.line_level_total_discount ?? null,
        };
      }

      pfCartMaterialState(cart) {
        const items = cart.items || [];
        const lines = items
          .map((item) => JSON.stringify(this.pfLineMaterialState(item, items)))
          .sort();
        return {
          attributes: this.normalizePfAttributes(cart.attributes || {}),
          cartDiscountApplications: this.pfStableValue(cart.cart_level_discount_applications || []),
          currency: cart.currency || "",
          discountApplications: this.pfStableValue(cart.discount_applications || []),
          discountCodes: this.pfDiscountCodeState(cart),
          itemCount: Number(cart.item_count) || 0,
          itemsSubtotalPrice: cart.items_subtotal_price ?? null,
          lines,
          note: String(cart.note || ""),
          originalTotalPrice: cart.original_total_price ?? null,
          totalDiscount: cart.total_discount ?? null,
          totalPrice: cart.total_price ?? null,
        };
      }

      pfCurrentProductId(section) {
        return String(
          section?.querySelector("[data-pf-scroll-product-info]")?.dataset?.pfSelectedProductId ||
            section?.querySelector("[data-pf-adaptive-options]")?.dataset?.pfProductId ||
            ""
        );
      }

      pfExplicitWarrantyChoice() {
        const section = this.closest?.('[data-pf-experience="pf_scroll_pilot"]');
        const value = String(section?.dataset?.pfScrollWarrantyChoice || "");
        if (!value) return null;
        const choiceProductId = String(section.dataset.pfScrollWarrantyChoiceProductId || "");
        const currentProductId = this.pfCurrentProductId(section);
        if (choiceProductId && currentProductId && choiceProductId !== currentProductId)
          return null;
        return {
          noCoverage: value === "no_coverage",
          quoteItemId: value === "no_coverage" ? "" : value,
          variantId: String(section.dataset.pfScrollWarrantyChoiceVariantId || ""),
        };
      }

      /**
       * Reads the coverage choice from the quote the selector rendered. The quote is published on
       * the section element by pf-scroll-pilot-behavior.js and is only trusted while it still
       * matches the product and variant being added.
       */
      pfWarrantySelection(request = null) {
        const section = this.closest?.('[data-pf-experience="pf_scroll_pilot"]');
        const none = {
          eligible: false,
          explicitNoCoverage: false,
          explicitSelection: false,
          priceText: "",
          quote: null,
          quoteItemId: "",
          requiresCoverage: false,
          selectedPlan: null,
          title: "",
          year: "",
        };
        // A slow or failed SureBright quote must never block the sale. Until plans are on screen the
        // shopper cannot have chosen coverage, so the device is added without it.
        if (section?.dataset?.pfScrollWarrantyEligible === "pending") {
          this.pfTrackUncoveredAdd("quote_pending", request);
          return none;
        }
        const quote = section?.__pfWarrantyQuote?.quote;
        if (
          section?.dataset?.pfScrollWarrantyEligible !== "true" ||
          quote?.status !== "ok" ||
          !Array.isArray(quote.plans) ||
          !quote.plans.length
        )
          return none;

        const productId = this.pfCurrentProductId(section);
        const variantId = String(request?.mainVariantId || quote.variantId || "");
        if (String(quote.productId) !== productId || String(quote.variantId) !== variantId) {
          this.pfTrackUncoveredAdd("quote_stale", request);
          return none;
        }

        const explicitChoice = this.pfExplicitWarrantyChoice();
        const choiceIsCurrent = Boolean(explicitChoice && explicitChoice.variantId === variantId);
        const selectedButton = section.querySelector(
          "[data-pf-scroll-warranty-choice-value].is-selected"
        );
        const displayedValue = String(selectedButton?.dataset?.pfScrollWarrantyChoiceValue || "");
        const explicitNoCoverage = Boolean(
          choiceIsCurrent && explicitChoice.noCoverage && displayedValue === "no_coverage"
        );
        const selectedPlan =
          choiceIsCurrent &&
          !explicitChoice.noCoverage &&
          displayedValue === explicitChoice.quoteItemId
            ? quote.plans.find((plan) => plan.quoteItemId === explicitChoice.quoteItemId) || null
            : null;
        const year = String(selectedPlan?.years || "");
        return {
          ...none,
          eligible: true,
          explicitNoCoverage,
          explicitSelection: explicitNoCoverage || Boolean(selectedPlan),
          priceText:
            selectedButton
              ?.querySelector?.(".pf-scroll-warranty-selector__price")
              ?.textContent?.trim() || "",
          quote,
          quoteItemId: selectedPlan?.quoteItemId || "",
          requiresCoverage: Boolean(selectedPlan),
          selectedPlan,
          title: explicitNoCoverage
            ? "No Coverage"
            : selectedPlan
            ? window.PfSureBrightQuotes.formatTerm(year)
            : "",
          year,
        };
      }

      assertPfSureBrightDuplicateSafety(snapshot, request, warranty) {
        const mainVariantId = String(request?.mainVariantId || "");
        if (!mainVariantId || !Array.isArray(snapshot?.items)) return;

        const existingMainLines = snapshot.items.filter(
          (item) =>
            String(item.variant_id || item.id || "") === mainVariantId &&
            !this.pfHasWarrantyProperty(item)
        );
        if (!existingMainLines.length) return;

        const existingWarrantyLines = snapshot.items.filter((item) => {
          if (!this.pfHasWarrantyProperty(item)) return false;
          const coveredVariantId = Object.entries(item.properties || {}).find(
            ([name]) => name.toLowerCase() === "_variantid"
          )?.[1];
          return String(coveredVariantId || "") === mainVariantId;
        });
        if (!existingWarrantyLines.length) {
          if (warranty?.requiresCoverage) {
            throw this.pfCartError(
              "This product is already in your cart without SureBright coverage. Keep one coverage choice per product variant or remove the uncovered line before adding coverage."
            );
          }
          return;
        }
        if (!warranty?.requiresCoverage) {
          if (existingWarrantyLines.length) {
            throw this.pfCartError(
              "This product is already covered in your cart. Keep the same coverage for additional units or remove the covered line before adding No Coverage."
            );
          }
          return;
        }

        // SureBright issues new quote item IDs on every quote, so the same plan bought on an
        // earlier page load carries a different _sbQuoteItemId. Coverage matches on term.
        const selectedYear = String(warranty.year || "");
        const mismatchedLine = existingWarrantyLines.find(
          (item) => this.pfWarrantyLineYears(item) !== selectedYear
        );

        if (mismatchedLine) {
          const cartYears = this.pfWarrantyLineYears(mismatchedLine);
          const cartTerm = cartYears
            ? `${cartYears} ${cartYears === "1" ? "Year" : "Years"} of coverage`
            : "a different coverage plan";
          throw this.pfCartError(
            `This product is already in your cart with ${cartTerm}. Choose the same coverage to add another, or change the coverage in your cart first.`
          );
        }
      }

      pfTrackUncoveredAdd(reason, request) {
        window.PfSureBrightQuotes?.track("plug_warranty_atc_without_quote", {
          reason,
          variant_id: String(request?.mainVariantId || ""),
        });
      }

      pfWarrantyLineYears(item) {
        const term = String(
          Object.entries(item?.properties || {}).find(
            ([name]) => name.toLowerCase() === "term"
          )?.[1] || ""
        );
        return term.match(/\b([1-3])\s*[- ]?years?\b/i)?.[1] || "";
      }

      pfSureBrightPriceCents(value) {
        const normalized = String(value ?? "")
          .trim()
          .replace(/,/g, "");
        const match = normalized.match(/^(\d+)(?:\.(\d{1,2}))?$/);
        if (!match) return null;
        const cents = Number(match[1]) * 100 + Number(String(match[2] || "").padEnd(2, "0"));
        return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
      }

      pfFormatMoney(cents) {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }

      pfSureBrightResolutionSignature(resolution) {
        if (!resolution) return "";
        return JSON.stringify({
          expectedTotalCents: resolution.expectedTotalCents,
          expectedWarrantyLines: resolution.expectedWarrantyLines.map(
            ({
              componentQuantity,
              coveredVariantId,
              expectedUnitPriceCents,
              item,
              quoteItemId,
            }) => ({
              componentQuantity,
              coveredVariantId,
              expectedUnitPriceCents,
              item,
              quoteItemId,
            })
          ),
          schemaType: resolution.schemaType,
          selectedPolicy: {
            coverageYears: resolution.selectedPolicy?.coverageYears,
            quoteId: resolution.selectedPolicy?.quoteId,
            quoteItemId: resolution.selectedPolicy?.quoteItemId,
          },
        });
      }

      pfResolveSureBrightQuote(warranty, request, addedAt = "") {
        const section = this.closest?.('[data-pf-experience="pf_scroll_pilot"]');
        const unbound = () =>
          this.pfCartError(
            `SureBright coverage "${
              warranty.title || "selected offering"
            }" could not be matched to every covered item in one current quote. Refresh the page and choose coverage again. No products were added.`
          );
        if (!warranty.selectedPlan || !warranty.quote || !window.PfSureBrightQuotes)
          throw unbound();
        try {
          return window.PfSureBrightQuotes.buildWarrantyLineItems(
            warranty.quote,
            warranty.selectedPlan,
            {
              addedAt,
              mainQuantity: Number(request.mainItem?.quantity || 0),
              productId: this.pfCurrentProductId(section),
              productTitle:
                section
                  ?.querySelector?.(
                    ".pf-scroll-product-info__title h1, .pf-scroll-product-info__title h2"
                  )
                  ?.textContent?.trim() || "",
              variantId: String(request.mainVariantId || ""),
            }
          );
        } catch (error) {
          if (error?.code === "unsupported_schema") {
            throw this.pfCartError(
              `SureBright returned unsupported coverage schema "${error.message}". Refresh the page and choose coverage again. No products were added.`
            );
          }
          throw unbound();
        }
      }

      hydratePfSureBrightWarrantyLine(warranty, request) {
        if (!warranty.requiresCoverage) return;
        warranty.resolvedQuote = this.pfResolveSureBrightQuote(warranty, request);
        if (request.lines.some(({ item }) => this.pfHasWarrantyProperty(item))) return;
        warranty.resolvedQuote.expectedWarrantyLines.forEach(({ item }) => {
          request.lines.push({
            index: Number.POSITIVE_INFINITY,
            item,
            role: "warranty",
          });
          request.items.push(item);
        });
      }

      pfHasWarrantyProperty(item) {
        return Object.keys(item.properties || {}).some((name) =>
          /surebright|_sb|warranty|protection|coverage|policy|offering/i.test(name)
        );
      }

      pfWarrantyItemMatchesSelection(warranty, item, coveredVariantId = "") {
        if (!this.pfHasWarrantyProperty(item) || !warranty.quoteItemId) return false;
        const properties = Object.entries(item.properties || {});
        const quoteItemId = properties.find(
          ([name]) => name.toLowerCase() === "_sbquoteitemid"
        )?.[1];
        if (String(quoteItemId || "") !== warranty.quoteItemId) return false;
        const boundVariantId = properties.find(
          ([name]) => name.toLowerCase() === "_variantid"
        )?.[1];
        if (coveredVariantId && String(boundVariantId || "") !== String(coveredVariantId))
          return false;
        const term = properties.find(([name]) => name.toLowerCase() === "term")?.[1];
        if (!String(term || "").trim()) return false;
        const selectedYear =
          warranty.year || warranty.title?.match(/\b([1-3])\s*[- ]?years?\b/i)?.[1];
        const termYear = String(term).match(/\b([1-3])\s*[- ]?years?\b/i)?.[1];
        return Boolean(selectedYear && termYear && selectedYear === termYear);
      }

      pfWarrantyItemMatchesExpectedLine(expectedLine, item) {
        if (
          !expectedLine?.item ||
          String(item.variant_id || item.id || "") !== String(expectedLine.item.id)
        )
          return false;
        const actualProperties = this.normalizePfProperties(item.properties || {});
        const expectedProperties = this.normalizePfProperties(expectedLine.item.properties || {});
        return Object.entries(expectedProperties).every(
          ([name, value]) => actualProperties[name] === value
        );
      }

      buildPfWarrantyBinding(warranty, request) {
        const warrantyLines = request.lines.filter(({ item }) => this.pfHasWarrantyProperty(item));
        if (!warranty.eligible) {
          if (warrantyLines.length) {
            throw this.pfCartError(
              "SureBright supplied a coverage line for an ineligible product. No products were added."
            );
          }
          return { items: [], title: warranty.title };
        }
        if (warranty.eligible && !warranty.explicitSelection) {
          throw this.pfCartError(
            "Choose SureBright coverage or No Coverage before adding this configuration."
          );
        }
        if (warranty.explicitNoCoverage) {
          if (request.lines.some(({ item }) => this.pfHasWarrantyProperty(item))) {
            throw this.pfCartError(
              "SureBright still supplied a coverage line after No Coverage was selected. No products were added."
            );
          }
          return { items: [], title: warranty.title };
        }
        if (!warranty.requiresCoverage) return { items: [], title: warranty.title };
        if (!/^[1-3]$/.test(String(warranty.year || ""))) {
          throw this.pfCartError(
            "SureBright coverage must be a supported 1, 2, or 3 Year term. No products were added."
          );
        }
        if (!warranty.quoteItemId) {
          throw this.pfCartError(
            "SureBright did not provide the required _sbquoteitemid binding. No products were added."
          );
        }
        const resolution = warranty.resolvedQuote;
        if (!resolution?.expectedWarrantyLines?.length) {
          throw this.pfCartError(
            `SureBright coverage "${
              warranty.title || "selected offering"
            }" does not have a current quote binding. Refresh the page and choose coverage again. No products were added.`
          );
        }

        const entries = resolution.expectedWarrantyLines.map((expectedLine) => {
          const matches = warrantyLines.filter(
            ({ item }) =>
              this.pfWarrantyItemMatchesExpectedLine(expectedLine, item) &&
              Number(item.quantity) === Number(expectedLine.item.quantity)
          );
          return { expectedLine, matches };
        });
        const boundLines = entries.flatMap(({ matches }) => matches);
        if (
          warrantyLines.length !== resolution.expectedWarrantyLines.length ||
          entries.some(({ matches }) => matches.length !== 1) ||
          new Set(boundLines).size !== warrantyLines.length
        ) {
          const requirement =
            resolution.schemaType === "BUNDLE"
              ? "must have exactly one materially bound line for every covered item"
              : "must have exactly one materially bound product-form line";
          throw this.pfCartError(
            `SureBright coverage "${
              warranty.title || "selected offering"
            }" ${requirement}. No products were added.`
          );
        }
        boundLines.forEach((line) => {
          if (line.role === "integration") line.role = "warranty";
        });
        return {
          entries: entries.map(({ expectedLine, matches }) => ({
            expectedLine,
            item: matches[0].item,
          })),
          expectedTotalCents: resolution.expectedTotalCents,
          expectedWarrantyLines: resolution.expectedWarrantyLines,
          items: boundLines.map(({ item }) => item),
          schemaType: resolution.schemaType,
          selectedPolicy: resolution.selectedPolicy,
          title: warranty.title,
        };
      }

      assertPfWarrantySelectionStillCurrent(warranty, binding, request) {
        if (warranty.explicitNoCoverage) {
          const current = this.pfWarrantySelection(request);
          if (!current.explicitSelection || !current.explicitNoCoverage) {
            throw this.pfCartError(
              "SureBright could not confirm No Coverage before the cart update."
            );
          }
          return;
        }
        if (!warranty.requiresCoverage) return;
        const current = this.pfWarrantySelection(request);
        if (
          !current.explicitSelection ||
          !current.requiresCoverage ||
          current.title !== warranty.title ||
          current.year !== warranty.year ||
          current.quoteItemId !== warranty.quoteItemId
        ) {
          throw this.pfCartError(
            "SureBright could not confirm the selected coverage before the cart update."
          );
        }
        const currentResolution = this.pfResolveSureBrightQuote(
          current,
          request,
          warranty.resolvedQuote?.addedAt
        );
        if (
          this.pfSureBrightResolutionSignature(currentResolution) !==
          this.pfSureBrightResolutionSignature(warranty.resolvedQuote)
        ) {
          throw this.pfCartError(
            "SureBright coverage changed before the cart update. Refresh and choose coverage again. No products were added."
          );
        }
        if (
          binding.schemaType !== currentResolution.schemaType ||
          binding.expectedTotalCents !== currentResolution.expectedTotalCents
        ) {
          throw this.pfCartError(
            "SureBright coverage changed before the cart update. Refresh and choose coverage again. No products were added."
          );
        }
      }

      assertPfWarrantyStillSelected(warranty, binding, cart, snapshot, request) {
        if (warranty.explicitNoCoverage) {
          const current = this.pfWarrantySelection(request);
          if (!current.explicitSelection || !current.explicitNoCoverage) {
            throw this.pfCartError(
              "SureBright could not confirm No Coverage after the cart update."
            );
          }
          return;
        }
        if (!warranty.requiresCoverage) return;
        this.assertPfWarrantySelectionStillCurrent(warranty, binding, request);
        const requestContext = [...(snapshot.items || []), ...request.items];
        const cartGroups = this.aggregatePfCartLines(cart.items || []);
        binding.entries.forEach(({ expectedLine, item: boundItem }) => {
          const signature = this.pfLineSignature(boundItem, requestContext);
          const lines = cartGroups.get(signature)?.lines || [];
          if (
            !lines.length ||
            !lines.some((line) => this.pfWarrantyItemMatchesExpectedLine(expectedLine, line))
          ) {
            throw this.pfCartError(
              `SureBright coverage "${warranty.title}" was not verifiably attached to the cart.`
            );
          }
        });
      }

      pfLineLabel(line) {
        if (line.role === "main") return "main product";
        if (line.role === "accessory") return `accessory variant ${line.item.id}`;
        if (line.role === "warranty") return `SureBright warranty variant ${line.item.id}`;
        return `integration variant ${line.item.id}`;
      }

      pfDisplayedPriceEvidence(request, warranty, binding) {
        const evidence = new Map();
        const scope = this.closest?.('[data-pf-experience="pf_scroll_pilot"]') || this;
        const mainPrices = Array.from(
          scope?.querySelectorAll?.("[data-pf-selected-variant-price]") || []
        )
          .map((node) => String(node.dataset.pfSelectedVariantPrice || "").trim())
          .filter(Boolean);
        if (mainPrices.length) {
          if (mainPrices.some((value) => !/^\d+$/.test(value)) || new Set(mainPrices).size !== 1) {
            throw this.pfCartError("The displayed main product price is invalid or inconsistent.");
          }
          evidence.set(request.mainItem, Number(mainPrices[0]));
        }

        request.lines
          .filter(({ role }) => role === "accessory")
          .forEach(({ item }) => {
            const option = scope?.querySelector?.(
              `[data-pf-scroll-complementary-option][data-variant-id="${CSS.escape(
                String(item.id)
              )}"]`
            );
            const value = String(option?.dataset?.variantPrice || "").trim();
            if (!value) return;
            if (!/^\d+$/.test(value)) {
              throw this.pfCartError(
                `The displayed accessory price for variant ${item.id} is invalid.`
              );
            }
            evidence.set(item, Number(value));
          });

        const warrantyMoney = String(warranty.priceText || "").match(
          /(?:\$)?([0-9,]+(?:\.\d{1,2})?)/
        )?.[1];
        if (warranty.requiresCoverage) {
          const warrantyPrice = this.pfSureBrightPriceCents(
            String(warrantyMoney || "").replace(/,/g, "")
          );
          if (
            !warrantyPrice ||
            !binding.entries?.length ||
            !Number.isFinite(binding.expectedTotalCents)
          ) {
            throw this.pfCartError(
              "The selected SureBright coverage does not have one valid displayed price and component binding. No products were added."
            );
          }
          if (warrantyPrice !== binding.expectedTotalCents) {
            throw this.pfCartError(
              `SureBright's ${
                warranty.title || "selected"
              } coverage changed from ${this.pfFormatMoney(warrantyPrice)} to ${this.pfFormatMoney(
                binding.expectedTotalCents
              )}. Refresh the page and choose coverage again. No products were added.`
            );
          }
          const componentTotalCents = binding.entries.reduce(
            (total, { expectedLine }) =>
              total +
              expectedLine.expectedUnitPriceCents * Number(expectedLine.componentQuantity || 1),
            0
          );
          if (componentTotalCents !== binding.expectedTotalCents) {
            throw this.pfCartError(
              `SureBright's ${
                warranty.title || "selected"
              } coverage could not be matched to its ${this.pfFormatMoney(
                binding.expectedTotalCents
              )} displayed total. No products were added.`
            );
          }
          binding.entries.forEach(({ expectedLine, item }) => {
            evidence.set(item, expectedLine.expectedUnitPriceCents);
          });
        }
        return evidence;
      }

      async preflightPfVariants(request, displayedPrices) {
        const variantIds = Array.from(new Set(request.items.map((item) => String(item.id))));
        const variants = await Promise.all(
          variantIds.map(async (variantId) => {
            const labels = request.lines
              .filter(({ item }) => String(item.id) === variantId)
              .map((line) => this.pfLineLabel(line))
              .join(", ");
            const variant = await this.pfFetchJson(
              this.pfCartUrl(`variants/${encodeURIComponent(variantId)}.js`),
              {
                cache: "no-store",
                headers: {
                  Accept: "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
              },
              `${labels || `Variant ${variantId}`} could not be revalidated.`
            );
            return { requestedVariantId: variantId, variant };
          })
        );
        const byId = new Map();
        variants.forEach(({ requestedVariantId, variant }) => {
          const affectedLines = request.lines.filter(
            ({ item }) => String(item.id) === requestedVariantId
          );
          const labels = affectedLines.map((line) => this.pfLineLabel(line)).join(", ");
          if (
            !variant?.id ||
            String(variant.id) !== requestedVariantId ||
            variant.available !== true ||
            !Number.isFinite(Number(variant.price))
          ) {
            throw this.pfCartError(
              `${labels || "A requested line"} is unavailable or has invalid live pricing.`
            );
          }
          byId.set(String(variant.id), variant);
        });
        request.lines.forEach((line) => {
          if (!displayedPrices.has(line.item)) return;
          const displayedPrice = Number(displayedPrices.get(line.item));
          const livePrice = Number(byId.get(String(line.item.id))?.price);
          if (displayedPrice !== livePrice) {
            throw this.pfCartError(
              `The displayed price for the ${this.pfLineLabel(
                line
              )} changed from ${this.pfFormatMoney(displayedPrice)} to ${this.pfFormatMoney(
                livePrice
              )}. No products were added.`
            );
          }
        });
        return byId;
      }

      assertPfPrices(cart, request, preflightVariants, snapshot) {
        const groups = this.aggregatePfCartLines(cart.items || []);
        const requestContext = [...(snapshot.items || []), ...request.items];
        request.lines.forEach((requestLine) => {
          const expectedPrice = Number(preflightVariants.get(String(requestLine.item.id))?.price);
          const lines =
            groups.get(this.pfLineSignature(requestLine.item, requestContext))?.lines || [];
          if (
            !lines.length ||
            lines.some((line) => Number(line.original_price ?? line.price) !== expectedPrice)
          ) {
            throw this.pfCartError(
              `The ${this.pfLineLabel(requestLine)} price changed while the cart was being built.`
            );
          }
        });
      }

      assertPfDisplayedWarrantyPrice(cart, request, displayedPrices, snapshot, binding) {
        const groups = this.aggregatePfCartLines(cart.items || []);
        const requestContext = [...(snapshot.items || []), ...request.items];
        const warrantyLines = request.lines.filter(({ role }) => role === "warranty");
        warrantyLines.forEach((requestLine) => {
          const expectedPrice = Number(displayedPrices.get(requestLine.item));
          if (!displayedPrices.has(requestLine.item) || !Number.isFinite(expectedPrice)) {
            throw this.pfCartError(
              "The selected SureBright coverage is missing valid displayed-price evidence."
            );
          }
          const lines =
            groups.get(this.pfLineSignature(requestLine.item, requestContext))?.lines || [];
          const priceMismatch =
            !lines.length ||
            lines.some((line) => {
              const quantity = Number(line.quantity || 0);
              const finalPrice = Number(line.final_price ?? line.discounted_price ?? line.price);
              const finalLinePrice = Number(line.final_line_price ?? finalPrice * quantity);
              return (
                !Number.isFinite(quantity) ||
                quantity <= 0 ||
                !Number.isFinite(finalPrice) ||
                !Number.isFinite(finalLinePrice) ||
                finalPrice !== expectedPrice ||
                finalLinePrice !== expectedPrice * quantity
              );
            });
          if (priceMismatch) {
            throw this.pfCartError(
              `SureBright did not keep the verified ${this.pfFormatMoney(
                expectedPrice
              )} price for warranty variant ${requestLine.item.id}.`
            );
          }
        });
        if (warrantyLines.length) {
          const requestedWarrantyTotal = warrantyLines.reduce(
            (total, { item }) =>
              total + Number(displayedPrices.get(item)) * Number(item.quantity || 0),
            0
          );
          const expectedWarrantyTotal =
            Number(binding?.expectedTotalCents) * Number(request.mainItem?.quantity || 0);
          if (
            !Number.isFinite(requestedWarrantyTotal) ||
            !Number.isFinite(expectedWarrantyTotal) ||
            requestedWarrantyTotal !== expectedWarrantyTotal
          ) {
            throw this.pfCartError(
              `SureBright did not price the selected coverage at the displayed ${this.pfFormatMoney(
                binding?.expectedTotalCents
              )} total.`
            );
          }
        }
      }

      assertPfCartSettingsUnchanged(cart, snapshot) {
        if (
          JSON.stringify(this.normalizePfAttributes(cart.attributes || {})) !==
          JSON.stringify(this.normalizePfAttributes(snapshot.attributes || {}))
        ) {
          throw this.pfCartError("Cart attributes changed during the product request.");
        }
        if (String(cart.note || "") !== String(snapshot.note || "")) {
          throw this.pfCartError("The cart note changed during the product request.");
        }
      }

      // Codes only: Shopify re-evaluates `applicable` whenever lines change (e.g. a minimum
      // subtotal is met by the add itself), so that flag is not evidence of a concurrent edit.
      pfDiscountCodeState(cart) {
        return (cart.discount_codes || [])
          .map(({ code }) => String(code || "").toUpperCase())
          .filter(Boolean)
          .sort();
      }

      pfCartSnapshotMatches(cart, snapshot) {
        return (
          JSON.stringify(this.pfCartMaterialState(cart)) ===
          JSON.stringify(this.pfCartMaterialState(snapshot))
        );
      }

      pfCartMaterialDifferences(cart, snapshot) {
        const current = this.pfCartMaterialState(cart);
        const expected = this.pfCartMaterialState(snapshot);
        return Object.keys(expected).filter(
          (key) => JSON.stringify(current[key]) !== JSON.stringify(expected[key])
        );
      }

      pfDiscountCodesMatch(cart, snapshot) {
        return (
          JSON.stringify(this.pfDiscountCodeState(cart)) ===
          JSON.stringify(this.pfDiscountCodeState(snapshot))
        );
      }

      assertPfRollbackExpectedState(cart, snapshot, request) {
        this.validatePfSnapshotRelationships(cart);
        this.assertPfCartQuantities(
          cart,
          this.expectedPfCartQuantities(snapshot, request?.items || []),
          "post-add cart"
        );
        this.assertPfCartSettingsUnchanged(cart, snapshot);
        if (!this.pfDiscountCodesMatch(cart, snapshot)) {
          throw this.pfCartError("Cart discount codes changed during the product request.");
        }
      }

      pfTrustedAddResponseGroups(addResponse, request) {
        const responseItems = addResponse?.items;
        if (
          !Array.isArray(responseItems) ||
          !responseItems.length ||
          responseItems.some((item) => !item?.key || !(item.variant_id || item.id))
        ) {
          throw this.pfCartError(
            "Shopify did not return trusted line keys for automatic cart rollback."
          );
        }

        const requestGroups = this.aggregatePfCartLines(request.items || []);
        const responseGroups = this.aggregatePfCartLines(responseItems);
        if (
          requestGroups.size !== responseGroups.size ||
          Array.from(requestGroups.keys()).some((signature) => !responseGroups.has(signature))
        ) {
          throw this.pfCartError(
            "Shopify's add response did not identify the exact requested cart lines."
          );
        }
        return { requestGroups, responseGroups };
      }

      async restorePfCartWithTargetedUpdates(snapshot, current, request, addResponse) {
        this.assertPfRollbackExpectedState(current, snapshot, request);
        const { requestGroups, responseGroups } = this.pfTrustedAddResponseGroups(
          addResponse,
          request
        );
        const currentGroups = this.aggregatePfCartLines(current.items || []);
        const snapshotKeys = new Set(
          (snapshot.items || []).map((item) => item.key).filter(Boolean)
        );
        const updates = {};

        requestGroups.forEach((requestGroup, signature) => {
          let quantityToRemove = requestGroup.quantity;
          const trustedKeys = new Set(
            (responseGroups.get(signature)?.lines || []).map((line) => line.key).filter(Boolean)
          );
          const candidates = (currentGroups.get(signature)?.lines || [])
            .filter((line) => trustedKeys.has(line.key))
            .sort((left, right) => {
              const leftWasExisting = snapshotKeys.has(left.key) ? 1 : 0;
              const rightWasExisting = snapshotKeys.has(right.key) ? 1 : 0;
              return leftWasExisting - rightWasExisting;
            });

          candidates.forEach((line) => {
            if (quantityToRemove <= 0) return;
            const currentQuantity = Number(line.quantity);
            if (!line.key || !Number.isInteger(currentQuantity) || currentQuantity < 1) {
              throw this.pfCartError(
                "A trusted Shopify cart line had an invalid key or quantity during rollback."
              );
            }
            const removedQuantity = Math.min(currentQuantity, quantityToRemove);
            const restoredQuantity = currentQuantity - removedQuantity;
            updates[line.key] = restoredQuantity;
            quantityToRemove -= removedQuantity;
          });

          if (quantityToRemove !== 0) {
            throw this.pfCartError(
              "The newly added cart quantities could not be isolated from existing lines."
            );
          }
        });

        if (!Object.keys(updates).length) {
          throw this.pfCartError(
            "No trusted newly added cart quantities were available to remove."
          );
        }

        await this.pfFetchJson(
          routes.cart_update_url,
          this.pfJsonRequest({ updates }),
          "The cart rollback could not restore its quantities."
        );
        return this.fetchPfCart();
      }

      async rollbackPfCart(snapshot, request, addResponse = null) {
        const concurrentMutationError = () =>
          this.pfCartError(
            "The cart changed outside this product request. Automatic rollback was refused to avoid deleting another cart change. Refresh and review the cart manually."
          );
        const current = await this.fetchPfCart();
        if (this.pfCartSnapshotMatches(current, snapshot)) return current;
        if (!addResponse || !this.pfRollbackStateIsRecognized(current, snapshot, request)) {
          throw concurrentMutationError();
        }

        const restored = await this.restorePfCartWithTargetedUpdates(
          snapshot,
          current,
          request,
          addResponse
        );
        if (!this.pfCartSnapshotMatches(restored, snapshot)) {
          const differences = this.pfCartMaterialDifferences(restored, snapshot);
          throw this.pfCartError(
            `The cart could not be restored exactly${
              differences.length ? ` (${differences.join(", ")})` : ""
            }. Refresh the page and review the cart before continuing.`
          );
        }
        return restored;
      }

      pfRequestedSections() {
        if (!this.cart) return [];
        const sections = this.cart.getSectionsToRender().map((section) => ({
          id: String(section.id || ""),
          selector: String(section.selector || ".shopify-section"),
        }));
        if (sections.some(({ id, selector }) => !id || !selector)) {
          throw this.pfCartError("A requested cart section has no stable section ID.");
        }
        return sections;
      }

      pfResolvedRequestedSections(sections, cartItemKey) {
        return sections.map((section) => {
          if (section.id !== "cart-notification-product") return section;
          const escapedKey = String(cartItemKey || "")
            .replace(/\\/g, "\\\\")
            .replace(/"/g, '\\"');
          if (!escapedKey) {
            throw this.pfCartError(
              "The cart notification cannot be verified without the final product line key."
            );
          }
          return {
            ...section,
            selector: `[id="cart-notification-product-${escapedKey}"]`,
          };
        });
      }

      assertPfRequestedSections(response, sections) {
        if (!sections.length) return;
        const missing = sections.filter(
          ({ id }) => !response?.sections || response.sections[id] == null
        );
        if (missing.length) {
          throw this.pfCartError(
            `Cart sections failed to render: ${missing.map(({ id }) => id).join(", ")}.`
          );
        }
        const malformed = sections.filter(({ id, selector }) => {
          try {
            const parsed = new DOMParser().parseFromString(response.sections[id], "text/html");
            return !parsed.querySelector(selector);
          } catch {
            return true;
          }
        });
        if (malformed.length) {
          throw this.pfCartError(
            `Cart section HTML is missing its render target: ${malformed
              .map(({ id, selector }) => `${id} (${selector})`)
              .join(", ")}.`
          );
        }
      }

      pfMainAddedLine(addResponse, request) {
        const responseItems = addResponse?.items || [];
        const targetSignature = this.pfLineSignature(request.mainItem, request.items);
        const mainLine = responseItems.find(
          (item) => this.pfLineSignature(item, responseItems) === targetSignature
        );
        if (!mainLine?.id || !mainLine?.key) {
          throw this.pfCartError(
            "Shopify did not return the main product line ID and key for cart notification."
          );
        }
        return mainLine;
      }

      // The trade-in step saves cart attributes, which the commit would read as another tab's edit:
      // wait for its save to land, and hold new ones until the phone is in.
      async commitPfCartAtomically(formData, complementaryVariantIds) {
        const tradeIn = window.PlugTradeInPdp;
        await tradeIn?.whenIdle?.();
        const release = tradeIn?.hold?.();
        try {
          return await this.commitPfCartAtomicallyUnheld(formData, complementaryVariantIds);
        } finally {
          release?.();
        }
      }

      async commitPfCartAtomicallyUnheld(formData, complementaryVariantIds) {
        const initialSnapshot = await this.fetchPfCart();
        let snapshot = initialSnapshot;
        let mutationStarted = false;
        let addResponse = null;
        let request;
        try {
          this.validatePfSnapshotRelationships(initialSnapshot);
          request = this.buildPfAtomicItems(formData, complementaryVariantIds);
          this.validatePfParentReferences(request, initialSnapshot);
          const warranty = this.pfWarrantySelection(request);
          this.hydratePfSureBrightWarrantyLine(warranty, request);
          const warrantyBinding = this.buildPfWarrantyBinding(warranty, request);

          const displayedPrices = this.pfDisplayedPriceEvidence(request, warranty, warrantyBinding);
          const preflightVariants = await this.preflightPfVariants(request, displayedPrices);
          const sections = this.pfRequestedSections();
          const freshSnapshot = await this.fetchPfCart();
          if (!this.pfCartSnapshotMatches(freshSnapshot, initialSnapshot)) {
            throw this.pfCartError(
              "The cart changed in another tab before this request was committed. Review the cart and try again."
            );
          }
          snapshot = freshSnapshot;
          this.validatePfSnapshotRelationships(snapshot);
          this.validatePfParentReferences(request, snapshot);
          this.assertPfSureBrightDuplicateSafety(snapshot, request, warranty);
          this.assertPfWarrantySelectionStillCurrent(warranty, warrantyBinding, request);
          const sectionIds = sections.map(({ id }) => id);
          const payload = {
            items: request.items,
            ...(sections.length
              ? {
                  sections: sectionIds,
                  sections_url: window.location.pathname,
                }
              : {}),
          };

          mutationStarted = true;
          addResponse = await this.pfFetchJson(
            routes.cart_add_url,
            this.pfJsonRequest(payload),
            "The configured products could not be added together."
          );
          this.pfMainAddedLine(addResponse, request);
          const renderResponse = addResponse;
          const { cart: verifiedCart, line: verifiedMainLine } = await this.fetchPfCartAfterAdd(
            request,
            snapshot
          );
          this.assertPfPrices(verifiedCart, request, preflightVariants, snapshot);
          this.assertPfDisplayedWarrantyPrice(
            verifiedCart,
            request,
            displayedPrices,
            snapshot,
            warrantyBinding
          );
          this.assertPfCartSettingsUnchanged(verifiedCart, snapshot);
          if (!this.pfDiscountCodesMatch(verifiedCart, snapshot)) {
            throw this.pfCartError("Cart discount codes changed during the product request.");
          }
          this.assertPfWarrantyStillSelected(
            warranty,
            warrantyBinding,
            verifiedCart,
            snapshot,
            request
          );
          this.assertPfRequestedSections(
            renderResponse,
            this.pfResolvedRequestedSections(sections, verifiedMainLine.key)
          );

          this.trackPfWarrantyCommit(warranty, warrantyBinding, verifiedCart, request);
          return {
            ...renderResponse,
            ...verifiedCart,
            id: verifiedMainLine.id,
            key: verifiedMainLine.key,
            sections: renderResponse.sections,
          };
        } catch (error) {
          if (/coverage|surebright/i.test(String(error?.message || ""))) {
            window.PfSureBrightQuotes?.track("plug_warranty_atc_blocked", {
              cart_mutated: mutationStarted,
              message: String(error.message).slice(0, 160),
              variant_id: String(request?.mainVariantId || ""),
            });
          }
          if (!mutationStarted) throw error;
          try {
            await this.rollbackPfCart(snapshot, request, addResponse);
            error.pfRollbackComplete = true;
          } catch (rollbackError) {
            error.pfRollbackFailed = true;
            error.rollbackError = rollbackError;
          }
          throw error;
        }
      }

      trackPfWarrantyCommit(warranty, binding, cart, request) {
        const variantId = String(request.mainVariantId || "");
        const items = cart.items || [];
        const coveredUnits = items
          .filter(
            (item) =>
              this.pfHasWarrantyProperty(item) &&
              String(item.properties?._variantId || "") === variantId
          )
          .reduce((total, item) => total + Number(item.quantity || 0), 0);
        const deviceUnits = items
          .filter(
            (item) =>
              !this.pfHasWarrantyProperty(item) &&
              String(item.variant_id || item.id || "") === variantId
          )
          .reduce((total, item) => total + Number(item.quantity || 0), 0);
        if (warranty.requiresCoverage) {
          window.PfSureBrightQuotes?.track("plug_warranty_attached", {
            price_cents: binding.expectedTotalCents,
            quantity: Number(request.mainItem?.quantity || 0),
            schema_type: binding.schemaType,
            variant_id: variantId,
            years: Number(warranty.year),
          });
        }
        // Repeat covered adds leave one policy line per add, so compare units, not lines.
        if (binding.schemaType !== "BUNDLE" && coveredUnits > deviceUnits) {
          window.PfSureBrightQuotes?.track("plug_warranty_sdk_conflict", {
            covered_units: coveredUnits,
            device_units: deviceUnits,
            variant_id: variantId,
          });
        }
      }

      handlePfAtomicCartError(error, formData) {
        const rollbackFailed = error?.pfRollbackFailed === true;
        const message = rollbackFailed
          ? error.rollbackError?.message ||
            "The cart could not be restored exactly. Refresh and review the cart before continuing."
          : error?.response?.description || error?.message || "The cart could not be updated.";
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: message,
          message,
        });
        this.handleErrorMessage(message);
        this.error = rollbackFailed;
      }

      addComplementaryProducts(complementaryVariantIds) {
        if (!complementaryVariantIds.length) return Promise.resolve();

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.body = JSON.stringify({
          items: complementaryVariantIds.map((variantId) => ({
            id: variantId,
            quantity: 1,
          })),
        });

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const error = new Error(response.description || response.message);
              error.response = response;
              throw error;
            }
            return response;
          });
      }
    }
  );
}
